/* La raccolta dei tasti (§ 07), in un posto solo.
 *
 * **Le mani sono separate, ed e' la regola che regge tutto il resto.** La
 * navigazione sta sotto la destra (`J K L H ;`), i verdetti sotto la sinistra
 * (`A S D 1…4`). Un dito che sbaglia mentre scorre non puo' cosi' lasciare un
 * verdetto, e un verdetto non puo' spostare l'elenco. E' questo che permette
 * di non chiedere conferme: senza la separazione, un dialogo servirebbe
 * davvero.
 *
 * **Annullamento invece di conferma.** Il verdetto si applica subito. Per
 * cinque secondi resta in un angolo il contatore con `Ctrl+Z`. Su seicento
 * anomalie un dialogo di conferma costa al turno una quarantina di minuti; la
 * finestra di annullamento non costa niente e prende lo stesso errore.
 *
 * **La tabella e' una sola.** Il gestore dei tasti e la mappa che si apre con
 * `?` leggono queste righe: due elenchi separati divergono al primo tasto
 * spostato, e l'operatore impara quello sbagliato.
 *
 * **Questa tabella dice che cosa fa un tasto, non quando.** Lo *quando* e' del
 * Cockpit, e vale la pena scriverlo: il gestore vive sulla radice della tela e
 * non su `window`, quindi un tasto premuto mentre il fuoco e' altrove — barra
 * dell'indirizzo, intestazione della shell, un campo di testo — non arriva
 * affatto qui. Senza quel confine `A` confermerebbe un candidato mentre
 * l'operatore sta scrivendo altrove.
 *
 * La raccolta e' cambiata tutta insieme con ADR-004. Prima: `A` confermava,
 * `1/2/3` erano il livello, `R`+cifra rifiutava, le frecce scorrevano. Farlo a
 * meta' — due mappe mescolate — sarebbe stato peggio della vecchia.
 */

import type { MessageKey } from './i18n/messages'

export type Action =
  | { kind: 'confirm'; rank: number | null }
  | { kind: 'reject'; shortcut: number }
  | { kind: 'falsePositive' }
  /** Restituisce alla coda un candidato su cui non si e' deciso niente. */
  | { kind: 'defer' }
  /** Apre la ricerca libera: cio' che il modello non ha visto. */
  | { kind: 'manual' }
  /** Apre la scheda dell'elemento su cui sta il candidato: tutto cio' che
   *  riguarda quel palo, in un posto solo. */
  | { kind: 'element' }
  | { kind: 'undo' }
  | { kind: 'next' }
  | { kind: 'previous' }
  /** Un passo nel nastro dei fotogrammi (ADR-006): il fotogramma dopo o prima
   *  nell'ordine del volo, della stessa camera. Il codice di disegno § 07
   *  diceva «della stessa anomalia», ma quel fotogramma spesso non esiste, e un
   *  tasto che risponde una volta su dieci non si impara. */
  | { kind: 'nextFrame' }
  | { kind: 'previousFrame' }
  | { kind: 'draw' }
  /** Il contorno a vertici (GI-228): un clic per vertice, Invio per chiudere. */
  | { kind: 'polygon' }
  | { kind: 'zoom'; how: 'uno-a-uno' | 'inquadra' | 'trovata' }
  | { kind: 'overlays'; visible: boolean }
  | { kind: 'ranking' }
  | { kind: 'rejecting' }
  | { kind: 'panel'; side: 'coda' }
  | { kind: 'help' }
  | { kind: 'cancel' }
  | null

/** In quale stato si trova la tastiera. Uno stato e non una combinazione da
 *  tenere premuta: chi decide centinaia di candidati tiene la mano ferma. */
export type Mode = 'normale' | 'motivo' | 'rango'

export type Hand = 'destra' | 'sinistra' | 'tela' | 'globale'

export interface Binding {
  keys: string[]
  hand: Hand
  what: MessageKey
}

/** Quel che si vede nella mappa dei tasti, nell'ordine in cui si impara. */
export const BINDINGS: Binding[] = [
  { keys: ['J'], hand: 'destra', what: 'keys.nextFinding' },
  { keys: ['K'], hand: 'destra', what: 'keys.previousFinding' },
  { keys: ['L'], hand: 'destra', what: 'keys.nextFrame' },
  { keys: ['H'], hand: 'destra', what: 'keys.previousFrame' },
  // La scheda e' orientamento e non verdetto: sta sulla mano che scorre, e
  // premerla per sbaglio costa una pagina, non una decisione.
  { keys: ['O'], hand: 'destra', what: 'keys.openElement' },

  { keys: ['A'], hand: 'sinistra', what: 'keys.confirm' },
  { keys: ['S'], hand: 'sinistra', what: 'keys.rankMode' },
  { keys: ['D'], hand: 'sinistra', what: 'keys.falsePositive' },
  { keys: ['F'], hand: 'sinistra', what: 'keys.defer' },
  { keys: ['G'], hand: 'sinistra', what: 'keys.manual' },
  // Nel gesto singolo S1 manca di proposito: la Specifica Tecnica ha tre
  // livelli, la scala ne ha cinque, e confermare a S1 dichiarerebbe un livello
  // che il committente non conosce. Dopo «S» il tasto risponde comunque, e la
  // schermata dice perche' non puo' confermare: un tasto muto si ribatte, una
  // spiegazione si legge una volta.
  { keys: ['2', '3', '4'], hand: 'sinistra', what: 'keys.confirmRank' },
  // Il rifiuto con un motivo dichiarato non sta nel codice di disegno, che
  // conosce il solo falso positivo. Da noi esiste, e tiene il tasto su cui gli
  // operatori sono gia' addestrati: `R` sta sulla stessa mano dei verdetti.
  { keys: ['R'], hand: 'sinistra', what: 'keys.rejectReason' },
  { keys: ['B'], hand: 'tela', what: 'keys.redrawBox' },
  { keys: ['P'], hand: 'tela', what: 'keys.drawPolygon' },
  { keys: ['Z'], hand: 'tela', what: 'keys.zoomOne' },
  { keys: ['X'], hand: 'tela', what: 'keys.zoomFit' },
  { keys: ['C'], hand: 'tela', what: 'keys.zoomFinding' },
  { keys: ['Spazio'], hand: 'tela', what: 'keys.hideOverlays' },

  { keys: ['Ctrl+Z'], hand: 'globale', what: 'keys.undo' },
  { keys: ['?'], hand: 'globale', what: 'keys.help' },
  // Il pannello della decisione non si chiude: il concetto approvato (§ 6.2)
  // vieta di nascondere lo stato della decisione mentre si confrontano
  // immagine e overlay. Resta la coda, che serve a orientarsi e non a decidere.
  { keys: [']'], hand: 'globale', what: 'keys.toggleQueue' },
  { keys: ['Esc'], hand: 'globale', what: 'keys.exitMode' },
]

export const HANDS: [Hand, MessageKey][] = [
  ['destra', 'keys.handRight'],
  ['sinistra', 'keys.handLeft'],
  ['tela', 'keys.handCanvas'],
  ['globale', 'keys.handGlobal'],
]

/**
 * Se l'azione cambia il lavoro invece di guardarlo.
 *
 * **Un elenco solo, letto da tutti.** Il gestore dei tasti lo usa per rifiutare
 * un verdetto in un banco che non puo' deciderne, i comandi visibili per
 * spegnersi. Erano due giudizi separati — `locked` nel pannello, niente nel
 * gestore — e divergevano: il bottone risultava spento e lo stesso verdetto
 * passava da tastiera. La differenza si scopriva dal giornale.
 *
 * Lo sguardo non e' qui dentro: scorrere, avvicinare, accendere un modo si
 * possono fare anche in sola osservazione, altrimenti il banco aperto da un
 * collegamento incollato non servirebbe a niente.
 */
export function mutating(action: Action): boolean {
  if (!action) return false
  return (
    action.kind === 'confirm' ||
    action.kind === 'reject' ||
    action.kind === 'falsePositive' ||
    action.kind === 'defer' ||
    action.kind === 'undo'
  )
}

/**
 * Se l'azione ha senso mentre il tasto si tiene premuto.
 *
 * **La tastiera ripete da sola, e la ripetizione non e' un gesto.** Un dito
 * appoggiato su `A` un istante di piu' del voluto manda una raffica di conferme
 * lungo il batch: l'annullamento vale una volta sola e non le disfa, e i
 * candidati passano con un verdetto che nessuno ha inteso dare. Vale per tutto
 * cio' che scrive un verdetto o rilascia un lease, e per lo scorrimento, che
 * porterebbe lo sguardo dove la mano non ha guardato.
 *
 * Il velo dei disegni e' l'eccezione perche' li' *tenere premuto e' il gesto*:
 * si nascondono gli overlay per guardare i pixel sotto, e si rivedono appena si
 * molla.
 */
export function held(action: Action): boolean {
  return action?.kind === 'overlays'
}

/**
 * Se questa battuta e' l'autorepeat della tastiera su un'azione che non lo
 * sopporta.
 *
 * **Un giudizio solo, perche' i confini sono due.** La ripetizione arriva da due
 * porte: il tasto battuto sulla tela e l'invio tenuto premuto sul comando
 * visibile che ha il fuoco. La tela leggeva `event.repeat` da se' e i comandi non
 * lo leggevano affatto: il fermo dei tasti c'era, e la stessa raffica di verdetti
 * rientrava dai bottoni. Due politiche sulla stessa cosa divergono, e la
 * differenza si scopre dal giornale.
 */
export function repeated(event: { repeat: boolean }, action: Action): boolean {
  return event.repeat && !held(action)
}

/**
 * Se il tasto e' quello che su un comando nativo diventa un clic **a ogni
 * pressione**.
 *
 * E' la mappa del navigatore, e la differenza conta: su un `<button>`
 * l'attivazione dell'invio e' l'azione predefinita del `keydown`, quindi
 * l'autorepeat produce un clic per ogni ripetizione — una raffica. Lo spazio si
 * attiva al `keyup`, quindi tenerlo premuto da' un clic solo: intercettarne la
 * ripetizione rischierebbe di annullare l'attivazione senza togliere nessuna
 * raffica.
 *
 * Con un modificatore non attiva niente, e `Ctrl+Z` resta l'annullamento anche
 * mentre il fuoco sta sul bottone appena premuto.
 */
export function clicking(event: {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
}): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false
  return event.key === 'Enter'
}

/**
 * Se il tasto e' l'attivazione del comando che ha il fuoco.
 *
 * Su un bottone lo spazio **e'** il clic, e l'invio lo stesso. Intercettarli
 * per la tela annullava l'attivazione nativa: il comando visibile non
 * rispondeva, e chi lavora a tastiera senza puntatore non aveva modo di
 * premerlo — cioe' un'azione critica raggiungibile solo dalla scorciatoia,
 * proprio cio' che il codice di disegno vieta.
 *
 * Con un modificatore no: `Ctrl+Z` resta l'annullamento anche mentre il fuoco
 * sta sul bottone appena premuto.
 */
export function activating(event: {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
}): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false
  return event.key === ' ' || event.key === 'Enter'
}

/**
 * Se l'elemento e' un comando nativo, che ha già i suoi tasti.
 *
 * Serve accanto a `activating`: da un bottone o da un collegamento lo spazio e
 * l'invio appartengono a lui, mentre le lettere restano scorciatoie — la mano
 * che ha appena premuto un comando deve poter continuare a battere i verdetti
 * senza tornare a cliccare sull'immagine.
 */
export function commanding(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName.toLowerCase()
  if (tag === 'button' || tag === 'summary') return true
  if (tag === 'a' && target.hasAttribute('href')) return true
  const role = target.getAttribute('role')
  return role === 'button' || role === 'link'
}

/**
 * Se il tasto appartiene a chi scrive invece che a chi decide.
 *
 * Dentro un campo di testo «a» e' una lettera, non una conferma. Il confine sta
 * qui e non nella schermata perche' e' una regola, e una regola si prova senza
 * montare niente: nel montato si vedrebbe soltanto il caso che si e' costruito.
 *
 * Un bottone non conta: e' un comando, e la mano che lo ha appena premuto deve
 * poter continuare a battere i verdetti senza tornare a cliccare sull'immagine.
 * Lo spazio e l'invio sono l'eccezione, e stanno in `activating`.
 */
export function editable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  // L'attributo e non solo `isContentEditable`: quest'ultimo dipende
  // dall'impaginazione, e sotto jsdom non c'e' impaginazione — la regola
  // resterebbe non provata proprio nel caso piu' facile da sbagliare.
  const written = target.getAttribute('contenteditable')
  if (target.isContentEditable || (written !== null && written !== 'false')) return true
  const tag = target.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select'
}

/** Che cosa fa un tasto. Separato dalla schermata perche' e' una regola, e le
 *  regole si provano senza montare niente. */
export function actionFor(
  event: {
    key: string
    shiftKey: boolean
    ctrlKey: boolean
    metaKey: boolean
    altKey: boolean
  },
  mode: Mode = 'normale',
): Action {
  const key = event.key.toLowerCase()
  const digit = Number(event.key)
  const isDigit = Number.isInteger(digit) && digit >= 1 && digit <= 9

  // L'annullamento e' l'unica combinazione che si intercetta, ed e' quella che
  // il codice di disegno nomina: fuori da un campo di testo `Ctrl+Z` nel
  // navigatore non fa niente, e qui e' la rete di sicurezza di tutti i
  // verdetti dati senza dialogo.
  if ((event.ctrlKey || event.metaKey) && key === 'z' && !event.altKey) return { kind: 'undo' }
  // Il resto delle combinazioni appartiene al sistema: `Ctrl+1` cambia scheda,
  // e rubarlo farebbe litigare la pagina col navigatore. A perderci sarebbe
  // sempre l'operatore.
  if (event.ctrlKey || event.metaKey || event.altKey) return null

  if (event.key === 'Escape') return { kind: 'cancel' }

  // Dopo `R` le cifre significano il motivo; dopo `S` significano il rango.
  // Due significati per lo stesso tasto vivono in due stati dichiarati, e la
  // schermata li mostra: dedurli sarebbe un verdetto sbagliato che aspetta.
  if (mode === 'motivo') return isDigit ? { kind: 'reject', shortcut: digit } : null
  // Dopo «S» tutta la scala risponde, S1 compreso: la correzione del rango e' un
  // gesto dichiarato, e un tasto che tace si ribatte finche' l'operatore crede
  // di aver sbagliato lui. Che S1 non abbia un livello da dichiarare lo dice la
  // schermata, con la sua ragione.
  if (mode === 'rango') {
    return isDigit && digit >= 1 && digit <= 4 ? { kind: 'confirm', rank: digit } : null
  }

  if (key === 'j') return { kind: 'next' }
  if (key === 'k') return { kind: 'previous' }
  if (key === 'l') return { kind: 'nextFrame' }
  if (key === 'h') return { kind: 'previousFrame' }
  if (key === 'o') return { kind: 'element' }

  if (key === 'a') return { kind: 'confirm', rank: null }
  if (key === 's') return { kind: 'ranking' }
  if (key === 'd') return { kind: 'falsePositive' }
  // Rinvio e trovata manuale stanno sulla mano dei verdetti perche' sono le due
  // uscite di chi ha guardato il candidato: una lo restituisce alla coda, l'altra
  // dichiara cio' che il modello non aveva visto.
  if (key === 'f') return { kind: 'defer' }
  if (key === 'g') return { kind: 'manual' }
  if (key === 'r') return { kind: 'rejecting' }
  if (key === 'b') return { kind: 'draw' }
  if (key === 'p') return { kind: 'polygon' }
  if (key === 'z') return { kind: 'zoom', how: 'uno-a-uno' }
  if (key === 'x') return { kind: 'zoom', how: 'inquadra' }
  if (key === 'c') return { kind: 'zoom', how: 'trovata' }
  // Si tiene premuto invece di commutare: nascondere i disegni serve a
  // guardare i pixel sotto, e chi guarda vuole rivederli appena molla il
  // tasto, non ricordarsi di riaccenderli.
  if (event.key === ' ') return { kind: 'overlays', visible: false }

  if (event.key === '?') return { kind: 'help' }
  if (event.key === ']') return { kind: 'panel', side: 'coda' }

  // Il rango e la conferma in un gesto solo: e' la decisione piu' frequente
  // della giornata.
  if (isDigit && digit >= 2 && digit <= 4) return { kind: 'confirm', rank: digit }
  return null
}
